/**
 * The Reports home — DESIGN-reports.md decision L (step 0b, 23/09/2026).
 *
 * Four headline numbers for the leave year, then one card per report. Live cards open
 * their report; the ones not built yet show greyed as "coming" (Rob, 23/09: option a).
 * Everything here is cut to the viewer's reach on the SERVER — a manager sees the same
 * page for their own departments, and the Payroll and Setup health cards only arrive
 * for people allowed them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Banknote, ShieldCheck, ArrowRight, Scale, Timer, Thermometer, Coffee } from 'lucide-react';
import { toast } from 'sonner';
import { reportsApi, apiErrorMessage } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import { formatDuration } from '../../lib/approvalTimes';
import { ReportFrame, Card, Chip, PeriodPicker, NoAccess, fmtDays, HEX } from '../../components/reports/ReportUi';

const ICONS = {
  absence_over_time: TrendingUp, what_we_owe: Scale, managers_keeping_up: Timer,
  sickness: Thermometer, getting_a_break: Coffee,
  payroll: Banknote, setup_health: ShieldCheck,
};

const Stat = ({ label, value, sub, navy = false }) => (
  <Card className={navy ? 'relative overflow-hidden !bg-hive-navy !border-hive-navy text-white dark:!bg-hive-navy' : ''}>
    {navy && (
      <div className="absolute -right-5 -top-4 w-24 h-24 bg-hive-orange opacity-[.12]" style={{ clipPath: HEX }} />
    )}
    <p className={`text-[12.5px] font-bold ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{label}</p>
    <p className="font-poppins text-[32px] font-bold leading-tight">{value}</p>
    {sub && <p className={`text-xs ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{sub}</p>}
  </Card>
);

const MiniBars = ({ months }) => {
  const max = Math.max(1, ...months.map((m) => m.total || 0));
  return (
    <div className="flex items-end gap-1 h-10">
      {months.map((m, i) => (
        <i
          key={i}
          className="flex-1 rounded-t bg-hive-annual"
          style={{ height: `${Math.max(3, (100 * (m.total || 0)) / max)}%`, opacity: m.future ? 0.35 : 1 }}
          title={`${m.month}: ${fmtDays(m.total)} days`}
        />
      ))}
    </div>
  );
};

const ReportCard = ({ card }) => {
  if (card.status !== 'live') {
    return (
      <Card className="!bg-hive-tile dark:!bg-card border-dashed flex flex-col gap-1.5 min-h-[170px] h-full">
        <p className="font-poppins font-semibold text-[17px]">{card.title}</p>
        <p className="text-[13.5px] text-hive-muted dark:text-muted-foreground">{card.question}</p>
        <div className="mt-auto"><Chip>{card.badge || 'Coming soon'}</Chip></div>
      </Card>
    );
  }
  const Icon = ICONS[card.key] || TrendingUp;
  return (
    <Link to={card.path} className="block group h-full" data-testid={`report-card-${card.key}`}>
      <Card className="flex flex-col gap-1.5 min-h-[170px] h-full transition-shadow group-hover:shadow-[0_10px_30px_rgba(19,31,60,.10)]">
        <div className="w-10 h-10 bg-hive-pending-bg flex items-center justify-center" style={{ clipPath: HEX }}>
          <Icon className="w-5 h-5 text-hive-orange" />
        </div>
        <p className="font-poppins font-semibold text-[17px]">{card.title}</p>
        <p className="text-[13.5px] text-hive-muted dark:text-muted-foreground">{card.question}</p>
        <div className="mt-auto">
          {card.key === 'absence_over_time' && <MiniBars months={card.months || []} />}
          {card.key === 'what_we_owe' && (
            <>
              <p className="font-poppins font-bold text-[19px]">{fmtDays(card.untaken)} {Number(card.untaken) === 1 ? 'day' : 'days'} untaken</p>
              {(card.may_lose || 0) > 0
                ? <Chip tone="pending">{fmtDays(card.may_lose)} may be lost by {formatDateGB(card.year_end)}</Chip>
                : <Chip tone="success">Nothing at risk</Chip>}
            </>
          )}
          {card.key === 'managers_keeping_up' && (
            <>
              <p className="font-poppins font-bold text-[19px]">
                {card.decisions ? `Typically ${formatDuration(card.typical_hours)}` : 'No decisions yet'}
              </p>
              {card.stuck
                ? <Chip tone="danger">{card.stuck} stuck</Chip>
                : <Chip tone="success">{card.waiting ? `${card.waiting} waiting, none stuck` : 'Nothing waiting'}</Chip>}
            </>
          )}
          {card.key === 'sickness' && (
            card.no_sickness_type
              ? <Chip tone="pending">No leave type is marked as sickness</Chip>
              : (
                <>
                  <p className="font-poppins font-bold text-[19px]">{fmtDays(card.days)} sick {Number(card.days) === 1 ? 'day' : 'days'}</p>
                  <p className="text-xs text-hive-muted dark:text-muted-foreground mb-1">in the last 12 months</p>
                  {card.long_term > 0 && (
                    <Chip tone="pending">{card.long_term} long-term{card.long_term_off_now ? (card.long_term_off_now === card.long_term ? ', off now' : `, ${card.long_term_off_now} off now`) : ''}</Chip>
                  )}
                </>
              )
          )}
          {card.key === 'getting_a_break' && (
            <>
              <p className="font-poppins font-bold text-[19px]">{card.over} {card.over === 1 ? 'person' : 'people'}</p>
              <p className="text-xs text-hive-muted dark:text-muted-foreground mb-1">without a break for {card.threshold} days or more</p>
              {card.over > 0 && (
                card.over_nothing_booked === card.over
                  ? <Chip tone="pending">{card.over === 1 ? 'nothing booked' : 'none has a holiday booked'}</Chip>
                  : <Chip tone="success">{card.over - card.over_nothing_booked} {card.over - card.over_nothing_booked === 1 ? 'has' : 'have'} one booked</Chip>
              )}
            </>
          )}
          {card.key === 'payroll' && <p className="font-poppins font-bold text-[19px]">{card.figure}</p>}
          {card.key === 'setup_health' && (
            card.to_fix
              ? <p className="font-poppins font-bold text-[19px]"><Chip tone={card.red ? 'danger' : 'pending'}>{card.to_fix} {card.to_fix === 1 ? 'thing' : 'things'}</Chip> to fix</p>
              : <p className="font-poppins font-bold text-[19px]"><Chip tone="success">All sorted</Chip></p>
          )}
        </div>
        <p className="text-hive-orange font-extrabold text-[13.5px] inline-flex items-center gap-1">
          {card.key === 'setup_health' ? 'Opens Settings → Health' : 'Open'} <ArrowRight className="w-4 h-4" />
        </p>
      </Card>
    </Link>
  );
};

const ReportsHomePage = () => {
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.overview(year || undefined);
      setData(res.data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(apiErrorMessage(err, 'Could not load reports'));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (forbidden) return <ReportFrame><NoAccess /></ReportFrame>;

  const h = data?.headline;
  const end = data?.period?.end;

  return (
    <ReportFrame>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-poppins text-[26px] font-semibold">Reports</h1>
          <p className="text-sm text-hive-muted dark:text-muted-foreground">
            {data ? `${data.scope.label} · ${data.scope.people} ${data.scope.people === 1 ? 'person' : 'people'}` : 'Loading…'}
          </p>
        </div>
        {data && <PeriodPicker period={data.period} onYear={setYear} allowDates={false} />}
      </div>

      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3.5 my-5 ${loading ? 'opacity-60' : ''}`}>
        <Stat navy label="People" value={h ? fmtDays(h.people) : '–'}
              sub={h && h.left ? `incl. ${h.left} who left` : 'in this leave year'} />
        <Stat label="Days booked" value={h ? fmtDays(h.booked) : '–'} sub="approved, from allowances" />
        <Stat label="Days remaining" value={h ? fmtDays(h.remaining) : '–'}
              sub={h ? (h.pending ? `${fmtDays(h.pending)} more waiting for approval` : `to book by ${formatDateGB(end)}`) : ''} />
        <Stat label="Total allowance" value={h ? fmtDays(h.allowance) : '–'} sub="incl. carried over" />
      </div>

      {/* Every card the same size (Rob, 12/10/2026) — rows share the tallest card's height. */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 auto-rows-fr gap-3.5">
        {(data?.cards || []).map((c) => <ReportCard key={c.key} card={c} />)}
      </div>
    </ReportFrame>
  );
};

export default ReportsHomePage;
